/**
 * Frontend animation handler using Intersection Observer
 */

( function() {
	'use strict';
	
	// Cache selectors for better performance
	const SELECTORS = {
		sequential: '.ollie-animate-sequentially',
		onScroll: '.ollie-animate-on-scroll',
		hasAnimation: '.ollie-animate',
		animated: '.ollie-animated'
	};

	// Wait for DOM to be ready
	document.addEventListener( 'DOMContentLoaded', function() {
		// Check for reduced motion preference
		const prefersReducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;
		
		// If user prefers reduced motion, add class to all animated elements and exit
		if ( prefersReducedMotion ) {
			const allAnimatedElements = document.querySelectorAll( SELECTORS.hasAnimation );
			if ( allAnimatedElements.length > 0 ) {
				allAnimatedElements.forEach( function( element ) {
					element.classList.add( 'ollie-no-motion' );
				} );
			}
			// Skip all animation initialization
			return;
		}
		
		// Process sequential animations (copy parent animation to children)
		const sequentialContainers = document.querySelectorAll( SELECTORS.sequential );
		sequentialContainers.forEach( function( container ) {
			// Get animation classes from container
			const containerClasses = Array.from( container.classList );
			const animationClasses = containerClasses.filter( cls => 
				cls.startsWith( 'ollie-animate-' ) || 
				cls === 'ollie-animate' ||
				cls === 'ollie-animate-on-scroll' ||
				cls === 'ollie-animate-once' ||
				cls.startsWith( 'ollie-animation-preset-' )
			);
			
			// Get sequential delay
			const computedStyle = window.getComputedStyle( container );
			const sequentialDelay = parseFloat( computedStyle.getPropertyValue( '--sequential-delay' ) ) || 0.2;
			const baseDelay = parseFloat( computedStyle.getPropertyValue( '--animation-delay' ) ) || 0;
			
			// Check if this is a scroll animation
			const isScrollAnimation = containerClasses.includes( 'ollie-animate-on-scroll' );
			
			// Apply animation classes to children with staggered delays
			const children = container.children;
			Array.from( children ).forEach( function( child, index ) {
				// Copy animation CSS variables from parent
				const duration = computedStyle.getPropertyValue( '--animation-duration' );
				const distance = computedStyle.getPropertyValue( '--animation-distance' );
				const scale = computedStyle.getPropertyValue( '--animation-scale' );
				const scrollOffset = computedStyle.getPropertyValue( '--scroll-offset' );
				
				if ( duration ) child.style.setProperty( '--animation-duration', duration );
				if ( distance ) child.style.setProperty( '--animation-distance', distance );
				if ( scale ) child.style.setProperty( '--animation-scale', scale );
				if ( scrollOffset ) child.style.setProperty( '--scroll-offset', scrollOffset );
				
				// Set staggered delay
				// For scroll animations, apply sequential delay to first element too (index + 1)
				// For non-scroll animations, start from 0 (index)
				const delayMultiplier = isScrollAnimation ? ( index + 1 ) : index;
				const childDelay = baseDelay + ( sequentialDelay * delayMultiplier );
				child.style.setProperty( '--animation-delay', childDelay + 's' );
				
				// Add animation classes from parent
				animationClasses.forEach( cls => {
					if ( cls !== 'ollie-animate-sequentially' ) {
						child.classList.add( cls );
					}
				} );
			} );
			
			// Remove animation classes from parent to avoid double animation
			animationClasses.forEach( cls => {
				if ( cls !== 'ollie-animate-sequentially' ) {
					container.classList.remove( cls );
				}
			} );
		} );
		
		// Get all elements with scroll-based animations (including newly added children)
		const animatedElements = document.querySelectorAll( SELECTORS.onScroll );
		
		// Early return if no animated elements
		if ( ! animatedElements || animatedElements.length === 0 ) {
			return;
		}
		
		// Group elements by their scroll offset for efficient observer usage
		const elementsByOffset = new Map();
		const styleCache = new WeakMap();
		
		animatedElements.forEach( function( element ) {
			// Cache computed styles
			const computedStyle = window.getComputedStyle( element );
			styleCache.set( element, computedStyle );
			
			const scrollOffset = parseInt( computedStyle.getPropertyValue( '--scroll-offset' ) ) || -50;
			
			if ( ! elementsByOffset.has( scrollOffset ) ) {
				elementsByOffset.set( scrollOffset, [] );
			}
			elementsByOffset.get( scrollOffset ).push( element );
		} );
		
		// Create one observer per unique offset value
		elementsByOffset.forEach( function( elements, scrollOffset ) {
			const observerOptions = {
				threshold: 0.1,
				rootMargin: '0px 0px ' + scrollOffset + 'px 0px'
			};
			
			const observer = new IntersectionObserver( function( entries ) {
				// Batch DOM updates
				requestAnimationFrame( function() {
					entries.forEach( function( entry ) {
						if ( entry.isIntersecting ) {
							// Prepare for animation by adding will-change
							entry.target.classList.add( 'ollie-animate-ready' );
							
							// Small delay to ensure will-change takes effect
							setTimeout( function() {
								// Add animated class to trigger animation
								entry.target.classList.add( 'ollie-animated' );
								
								// Get animation duration from cached style or compute once
								const computedStyle = styleCache.get( entry.target ) || window.getComputedStyle( entry.target );
								const duration = parseFloat( computedStyle.getPropertyValue( '--animation-duration' ) ) || 1;
								const delay = parseFloat( computedStyle.getPropertyValue( '--animation-delay' ) ) || 0;
								const totalTime = ( duration + delay ) * 1000;
								
								// Remove will-change after animation completes for better performance
								setTimeout( function() {
									entry.target.classList.remove( 'ollie-animate-ready' );
									entry.target.classList.add( 'ollie-animation-done' );
								}, totalTime + 100 ); // Add small buffer
							}, 50 );
							
							// If animate once is set, stop observing this element
							if ( entry.target.classList.contains( 'ollie-animate-once' ) ) {
								observer.unobserve( entry.target );
							}
						} else {
							// Remove animated class if not set to animate once
							if ( ! entry.target.classList.contains( 'ollie-animate-once' ) ) {
								entry.target.classList.remove( 'ollie-animated', 'ollie-animate-ready', 'ollie-animation-done' );
							}
						}
					} );
				} );
			}, observerOptions );
			
			// Start observing all elements with this offset
			elements.forEach( function( element ) {
				observer.observe( element );
			} );
		} );
	} );
} )();